import { useState } from "react";
import { Navigate } from "react-router";
import { User, Send, ChevronLeft, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";
import { toast } from "sonner";

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.41 5.41 0 0 1 3.69 9c0-.6.1-1.18.28-1.72V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.32z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96L3.97 7.28C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

export default function LoginPage() {
  const { sendMagicLink, signInWithGoogle, session } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (session) {
    return <Navigate to="/pending" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await sendMagicLink(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd wysyłania linku");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // On success Supabase redirects to the OAuth provider; nothing more to do here.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nie udało się zalogować przez Google");
      setGoogleLoading(false);
    }
  };

  return (
    <AuthShell>
      {/* Brand mark */}
      <div className="mb-8 flex items-center gap-2.5">
        <span className="lf-mark-logo">
          <User className="h-[18px] w-[18px]" />
        </span>
        <div>
          <div className="font-brand text-lg font-bold text-pulso-primary">PulsO</div>
          <div className="-mt-0.5 text-[11.5px] text-pulso-muted">panel admina</div>
        </div>
      </div>

      {!sent ? (
        <>
          <div className="mb-6">
            <h1 className="font-brand text-[28px] font-bold leading-tight tracking-tight text-foreground">
              Zaloguj się
            </h1>
            <p className="mt-1.5 text-sm leading-relaxed text-pulso-muted">
              Wprowadź swój adres e-mail. Wyślemy ci jednorazowy link logowania.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Adres e-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                autoComplete="email"
                autoFocus
                required
                className="h-11 text-[15px]"
              />
              {error && <p className="text-[12.5px] text-pulso-orange">{error}</p>}
            </div>

            <Button
              type="submit"
              className="h-11 w-full gap-2 bg-pulso-primary text-[15px] font-semibold hover:bg-pulso-primary/90"
              disabled={loading}
            >
              {loading ? (
                "Wysyłanie..."
              ) : (
                <>
                  <Send className="h-4 w-4" /> Wyślij link logowania
                </>
              )}
            </Button>

            <div className="lf-divider my-1">
              <span>lub</span>
            </div>

            <Button
              type="button"
              variant="outline"
              className="h-11 w-full gap-2.5 border-[#dadce0] text-[15px] font-medium text-[#1f1f1f] hover:bg-[#f8f9fa]"
              onClick={handleGoogle}
              disabled={googleLoading}
            >
              <GoogleG />
              <span>{googleLoading ? "Przekierowanie..." : "Zaloguj się przez Google"}</span>
            </Button>
          </form>

          <div className="mt-6 border-t border-black/[0.06] pt-5 text-center">
            <span className="text-[12.5px] text-pulso-muted">
              Nie masz konta? Sprawdź stronę{" "}
              <a
                href="https://pulso.city/wspolpraca"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-pulso-primary hover:underline"
              >
                Współpraca
              </a>
            </span>
          </div>
        </>
      ) : (
        <div className="text-center">
          <div className="lf-sent-icon">
            <Send className="h-[26px] w-[26px]" />
          </div>
          <h2 className="mb-2 font-brand text-[22px] font-bold text-foreground">
            Sprawdź swoją skrzynkę
          </h2>
          <p className="text-sm leading-relaxed text-pulso-muted">
            Wysłaliśmy link logowania na <strong>{email}</strong>. Kliknij go z urządzenia, na
            którym chcesz się zalogować.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => {
                setSent(false);
                setEmail("");
              }}
            >
              <ChevronLeft className="h-4 w-4" /> Inny e-mail
            </Button>
            <Button
              variant="ghost"
              className="gap-1.5"
              onClick={() => {
                void sendMagicLink(email);
                toast.success("Link wysłany ponownie");
              }}
            >
              <RotateCcw className="h-4 w-4" /> Wyślij ponownie
            </Button>
          </div>
          <div className="mt-6 border-t border-black/[0.06] pt-5 text-center">
            <span className="text-[12.5px] text-pulso-muted">
              Nie widzisz wiadomości? Sprawdź folder „Spam".
            </span>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
